/**
 * Invoice List Helpers
 *
 * Pure helper functions and constants for the InvoiceList component.
 */

import type { InvoiceType, InvoiceStatus } from '../../../types/billing'

export function normalizeInvoiceType(type: string): InvoiceType {
  return type.toLowerCase().replace('invoice_type_', '') as InvoiceType
}

export function normalizeStatus(status: string): InvoiceStatus {
  return status.toLowerCase().replace('invoice_status_', '') as InvoiceStatus
}

export const INVOICE_TYPE_LABELS: Record<InvoiceType, string> = {
  b2b: 'B2B Tax Invoice',
  export: 'Export Invoice',
  b2c: 'B2C Invoice',
  credit_note: 'Credit Note',
  debit_note: 'Debit Note',
  vendor_bill: 'Vendor Bill',
  vendor_credit_note: 'Vendor Credit Note',
}

export const STATUS_LABELS: Record<InvoiceStatus, string> = {
  draft: 'Draft',
  posted: 'Posted',
  voided: 'Voided',
  cancelled: 'Cancelled',
}

export const typeFieldOptions = Object.entries(INVOICE_TYPE_LABELS).map(([value, label]) => ({ value, label }))
export const statusFieldOptions = Object.entries(STATUS_LABELS).map(([value, label]) => ({ value, label }))
export const paymentStatusFieldOptions = [
  { value: 'unpaid', label: 'Unpaid' },
  { value: 'partial', label: 'Partial' },
  { value: 'paid', label: 'Paid' },
  { value: 'written_off', label: 'Written Off' },
]

export function getTypeBadgeVariant(type: InvoiceType): 'secondary' | 'info' | 'warning' | 'error' {
  if (type === 'export') { return 'info' }
  if (type === 'credit_note' || type === 'vendor_credit_note') { return 'warning' }
  if (type === 'debit_note') { return 'error' }
  if (type === 'vendor_bill') { return 'info' }
  return 'secondary'
}

export function getStatusBadgeVariant(status: InvoiceStatus): 'secondary' | 'info' | 'error' {
  if (status === 'posted') { return 'info' }
  if (status === 'voided') { return 'error' }
  return 'secondary'
}

export function formatInvoiceDate(dateStr: string): string {
  if (!dateStr) { return '-' }
  const date = new Date(dateStr)
  return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
}

export function computeDueStatus(inv: { status: string; paymentStatus?: unknown; dueDate?: string }): 'overdue' | 'due-today' | null {
  const status = normalizeStatus(inv.status)
  if (status === 'draft' || status === 'voided') { return null }

  const payStatus = typeof inv.paymentStatus === 'string'
    ? inv.paymentStatus.toLowerCase().replace('payment_status_', '')
    : ''
  if (payStatus === 'paid' || payStatus === 'written_off') { return null }

  if (!inv.dueDate) { return null }

  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const dueDateStr = inv.dueDate
  const due = new Date(dueDateStr.includes('T') ? dueDateStr : `${dueDateStr}T00:00:00`)
  due.setHours(0, 0, 0, 0)
  const diffDays = Math.floor((today.getTime() - due.getTime()) / (1000 * 60 * 60 * 24))

  if (diffDays > 0) { return 'overdue' }
  if (diffDays === 0) { return 'due-today' }
  return null
}

export function getDueDiffDays(dueDate: string): number {
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const due = new Date(dueDate.includes('T') ? dueDate : `${dueDate}T00:00:00`)
  due.setHours(0, 0, 0, 0)
  return Math.floor((today.getTime() - due.getTime()) / (1000 * 60 * 60 * 24))
}

export function buildListTitle(isVendor: boolean, isProforma: boolean): string {
  if (isVendor) {
    return isProforma ? 'Draft Vendor Bills' : 'Posted Vendor Bills'
  }
  return isProforma ? 'Draft Invoices' : 'Posted Invoices'
}

export function buildEmptyStateDescription(hasFilters: boolean, isVendor: boolean): string {
  if (hasFilters) { return "Try adjusting your filters to find what you're looking for" }
  if (isVendor) { return 'Get started by recording your first vendor bill' }
  return 'Get started by creating your first invoice'
}
